/**
 * The Civil preview's five steps (docs/foundations.md §8): a house, from the
 * roof down. Models in web/src/lib/civil.ts; the engine is
 * components/tracks/engine.tsx, and the rule is Foundations': Check plays
 * the test out of the model before it says what happened.
 */
import { useState, type ComponentType } from 'react';
import { useLocale } from '../../state/useLocale';
import {
  BEAMS,
  BEAM_IDS,
  DEPTH_STOPS,
  EMPTY_WALL,
  IRIS_BEAM,
  IRIS_SPAN,
  LINTEL_KN_PER_M,
  LINTEL_SPAN,
  PANEL_KN,
  RAFTER_BUDGET,
  RAFTER_SPAN,
  ROOF_KPA,
  SOIL_KPA,
  SPACING_STOPS,
  WIDTH_STOPS,
  WINDOWS_WANTED,
  bracing,
  footing,
  lintel,
  rafters,
  type BeamId,
  type CivilStepId,
  type LintelResult,
  type Slot,
} from '../../lib/civil';
import { IrisSays, Shell, Stops, at, idx, useQ, useTested, type StepProps } from '../tracks/engine';
import { BRIEFS } from '../../lib/agent';

const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));

/** A crack, drawn where a member gave way. */
function Crack({ x, y, size = 10 }: { x: number; y: number; size?: number }) {
  return <path className="ffig__crack" d={`M ${x - 2} ${y - size} L ${x + 3} ${y - size / 3} L ${x - 3} ${y + size / 3} L ${x + 2} ${y + size}`} fill="none" />;
}

/* ── 1. Rafters ────────────────────────────────────────────────────────── */

function RafterFigure({ spacing, depth }: { spacing: number; depth: number }) {
  const { tested, p, phase } = useTested();
  const r = rafters(spacing, depth);
  const ratio = r.stressMpa / r.allowMpa;
  // Snow settles on the roof; the rafter below takes it.
  const snow = tested ? p : 0;
  const bend = tested ? Math.min(ratio, 1.6) * 14 * p : 0;
  const broke = tested && !r.strong && p > 0.75;
  const count = Math.round(3600 / spacing) + 1;
  const xs = Array.from({ length: count }, (_, k) => 20 + (k * 280) / (count - 1));
  const verdict = phase === 'pass' ? 'ffig--ok' : phase === 'fail' ? 'ffig--fail' : '';
  return (
    <svg className={`ffig ffig--roof ${verdict}`} viewBox="0 0 320 170" role="img" aria-hidden="true">
      {/* The roof from the gable end, snow building on it. */}
      <path className="ffig__roof" d="M 60 70 L 160 20 L 260 70 Z" />
      <path className="ffig__snow" opacity={snow} d={`M 60 ${70 - 2} L 160 ${20 - 6 * snow} L 260 ${70 - 2}`} strokeWidth={2 + 5 * snow} fill="none" />
      {tested &&
        [80, 110, 140, 175, 205, 235].map((x, k) => (
          <circle key={x} className="ffig__flake" cx={x} cy={(-10 + ((p * 90 + k * 13) % 60))} r={1.6} opacity={1 - p} />
        ))}
      {/* The rafters seen from above, at their spacing. */}
      {xs.map((x) => (
        <line key={x} className="ffig__rafter" x1={x} x2={x} y1={86} y2={104} strokeWidth={Math.max(2, (depth / 250) * 7)} />
      ))}
      {/* One rafter from the side, bending under the snow. */}
      <path className="ffig__plank" d={`M 30 140 Q 160 ${140 + 2 * bend} 290 140`} strokeWidth={Math.max(3, depth / 22)} fill="none" />
      <path className="ffig__bracket" d="M 22 146 L 38 146 L 30 160 Z" />
      <path className="ffig__bracket" d="M 282 146 L 298 146 L 290 160 Z" />
      {broke && <Crack x={160} y={140 + bend} />}
    </svg>
  );
}

function Rafters(props: StepProps) {
  const s = useLocale().strings.civil;
  const { q } = useQ();
  const [si, setSi] = useState(2);
  const [di, setDi] = useState(0);
  const spacing = at(SPACING_STOPS, si);
  const depth = at(DEPTH_STOPS, di);
  const r = rafters(spacing, depth);
  return (
    <Shell
      {...props}
      id="rafters" agent={{ briefs: BRIEFS['civil:rafters'] ?? [], apply: (p) => { if (p.spacing !== undefined) setSi(idx(SPACING_STOPS, p.spacing as (typeof SPACING_STOPS)[number])); if (p.depth !== undefined) setDi(idx(DEPTH_STOPS, p.depth as (typeof DEPTH_STOPS)[number])); } }}
      text={s.steps.rafters}
      version={`${si}:${di}`}
      evaluate={() => ({
        ok: r.ok,
        message: r.ok ? s.rafters.ok : !r.strong ? s.rafters.weak(q(r.stressMpa, 'MPa', 1)) : s.rafters.wasteful(q(r.timber * 1000, 'L/m')),
      })}
    >
      <div className="fsim">
        <p className="fread fread--quiet">
          {q(RAFTER_SPAN, 'm')} · {q(ROOF_KPA, 'kPa', 1)} · 50 mm
        </p>
        <RafterFigure spacing={spacing} depth={depth} />
        <p className="fread">
          <strong>{s.rafters.stress(q(r.stressMpa, 'MPa', 1), q(r.allowMpa, 'MPa', 1))}</strong> ·{' '}
          {s.rafters.timber(q(r.timber * 1000, 'L/m', 1), q(RAFTER_BUDGET * 1000, 'L/m'))}
        </p>
        <Stops label={s.rafters.spacing} stops={SPACING_STOPS} index={si} onChange={setSi} format={(v) => q(v, 'mm')} />
        <Stops label={s.rafters.depth} stops={DEPTH_STOPS} index={di} onChange={setDi} format={(v) => q(v, 'mm')} />
      </div>
    </Shell>
  );
}

/* ── 2 and 5. A beam over an opening ───────────────────────────────────── */

function LintelFigure({ beam, span, result }: { beam: BeamId; span: number; result: LintelResult }) {
  const { tested, p, phase } = useTested();
  const width = span * 52;
  const left = 160 - width / 2;
  const right = 160 + width / 2;
  const top = 64;
  const h = Math.max(6, BEAMS[beam].depth / 22);
  const sag = tested ? Math.min(result.sagMm / result.limitMm, 2.2) * 9 * p : 0;
  const broke = tested && !result.strong && p > 0.7;
  const verdict = phase === 'pass' ? 'ffig--ok' : phase === 'fail' ? 'ffig--fail' : '';
  return (
    <svg className={`ffig ffig--lintel ${verdict} is-${BEAMS[beam].material}`} viewBox="0 0 320 160" role="img" aria-hidden="true">
      <rect className="ffig__wall" x={10} y={10} width={300} height={150} />
      {/* The load from above, arriving on Check. */}
      {Array.from({ length: 7 }, (_, k) => {
        const x = left + 10 + (k * (width - 20)) / 6;
        const y = tested ? 14 + (top - 34) * p : 14;
        return <path key={k} className="ffig__arrow" d={`M ${x} ${y} L ${x} ${y + 18} M ${x - 4} ${y + 13} L ${x} ${y + 18} L ${x + 4} ${y + 13}`} fill="none" />;
      })}
      <path
        className="ffig__beamline"
        d={`M ${left - 14} ${top} L ${left} ${top} Q 160 ${top + 2 * sag} ${right} ${top} L ${right + 14} ${top}`}
        strokeWidth={h}
        fill="none"
      />
      <rect className="ffig__opening" x={left} y={top + h / 2 + sag * 0.9} width={width} height={160 - top - h / 2 - sag * 0.9} />
      <rect className="ffig__garage" x={left + 6} y={top + h / 2 + 10 + sag} width={width - 12} height={150 - top - h - 10 - sag} />
      {broke && <Crack x={160} y={top + sag} size={h + 4} />}
    </svg>
  );
}

function BeamPicker({ index, onChange }: { index: number; onChange(i: number): void }) {
  const s = useLocale().strings.civil;
  return <Stops label={s.lintel.beam} stops={BEAM_IDS} index={index} onChange={onChange} format={(b) => s.beams[b]} />;
}

function LintelReadout({ result }: { result: LintelResult }) {
  const s = useLocale().strings.civil.lintel;
  const { q } = useQ();
  return (
    <p className="fread">
      <strong>{s.stress(q(result.stressMpa, 'MPa'), q(result.allowMpa, 'MPa'))}</strong> ·{' '}
      {s.sag(q(result.sagMm, 'mm', 1), q(result.limitMm, 'mm', 1))}
    </p>
  );
}

const lintelMessage = (r: LintelResult, s: ReturnType<typeof useLocale>['strings']['civil']['lintel']) =>
  r.ok ? s.ok : !r.strong ? s.weak : s.bouncy;

function Lintel(props: StepProps) {
  const s = useLocale().strings.civil;
  const { q } = useQ();
  const [i, setI] = useState(0);
  const beam = at(BEAM_IDS, i);
  const r = lintel(beam, LINTEL_SPAN);
  return (
    <Shell {...props} id="lintel" agent={{ briefs: BRIEFS['civil:lintel'] ?? [], apply: (p) => { if (p.beam) setI(idx(BEAM_IDS, p.beam as BeamId)); } }} text={s.steps.lintel} version={String(i)} evaluate={() => ({ ok: r.ok, message: lintelMessage(r, s.lintel) })}>
      <div className="fsim">
        <p className="fread fread--quiet">
          {s.lintel.opening(q(LINTEL_SPAN, 'm', 1))} · {q(LINTEL_KN_PER_M, 'kN/m')}
        </p>
        <LintelFigure beam={beam} span={LINTEL_SPAN} result={r} />
        <LintelReadout result={r} />
        <BeamPicker index={i} onChange={setI} />
      </div>
    </Shell>
  );
}

function CivilAi(props: StepProps) {
  const s = useLocale().strings.civil;
  const { q } = useQ();
  const [i, setI] = useState(BEAM_IDS.indexOf(IRIS_BEAM));
  const beam = at(BEAM_IDS, i);
  const r = lintel(beam, IRIS_SPAN);
  return (
    <Shell {...props} id="civil-ai" agent={{ briefs: BRIEFS['civil:civil-ai'] ?? [], apply: (p) => { if (p.beam) setI(idx(BEAM_IDS, p.beam as BeamId)); } }} text={s.steps['civil-ai']} version={String(i)} evaluate={() => ({ ok: r.ok, message: lintelMessage(r, s.lintel) })}>
      <div className="fsim">
        <IrisSays>{s.iris}</IrisSays>
        <p className="fread fread--quiet">
          {s.lintel.opening(q(IRIS_SPAN, 'm', 1))} · {q(LINTEL_KN_PER_M, 'kN/m')}
        </p>
        <LintelFigure beam={beam} span={IRIS_SPAN} result={r} />
        <LintelReadout result={r} />
        <BeamPicker index={i} onChange={setI} />
      </div>
    </Shell>
  );
}

/* ── 3. Footings ───────────────────────────────────────────────────────── */

function FootingFigure({ front, back }: { front: number; back: number }) {
  const { tested, p, phase } = useTested();
  const f = footing(front, SOIL_KPA.front);
  const b = footing(back, SOIL_KPA.back);
  // Settlement: a footing within its soil's limit sinks a little; one over it
  // keeps going. The house tilts toward whichever end sank more.
  const settle = (r: typeof f) =>
    tested ? p * Math.min(r.ok ? 2 + 3 * (r.pressureKpa / r.allowKpa) : 10 + 20 * (r.pressureKpa / r.allowKpa - 1), 22) : 0;
  const sf = settle(f);
  const sb = settle(b);
  const xl = 80;
  const xr = 240;
  // A few degrees is already a house nobody can live in; more is a cartoon.
  const tilt = Math.max(-6, Math.min(6, (Math.atan2(sb - sf, xr - xl) * 180) / Math.PI));
  const ground = 100;
  const verdict = phase === 'pass' ? 'ffig--ok' : phase === 'fail' ? 'ffig--fail' : '';
  const pad = (x: number, w: number, s: number, r: typeof f) => {
    const px = w * 90;
    const bulb = Math.min(r.pressureKpa / r.allowKpa, 1.8);
    return (
      <g>
        {tested && <ellipse className={r.ok ? 'ffig__bulb' : 'ffig__bulb is-over'} cx={x} cy={ground + 18 + s} rx={(px / 2) * (0.6 + 0.6 * p)} ry={34 * bulb * p} />}
        <rect className="ffig__footing" x={x - px / 2} y={ground + 8 + s} width={px} height={14} />
      </g>
    );
  };
  return (
    <svg className={`ffig ffig--footing ${verdict}`} viewBox="0 0 320 170" role="img" aria-hidden="true">
      <rect className="ffig__soil" x={0} y={ground} width={160} height={70} />
      <rect className="ffig__soil is-soft" x={160} y={ground} width={160} height={70} />
      {pad(xl, front, sf, f)}
      {pad(xr, back, sb, b)}
      <g transform={`rotate(${tilt} ${sb > sf ? xl : xr} ${ground + Math.min(sf, sb)})`}>
        <rect className="ffig__house" x={xl - 6} y={36 + Math.min(sf, sb)} width={xr - xl + 12} height={ground - 28} />
        <path className="ffig__roof" d={`M ${xl - 16} ${40 + Math.min(sf, sb)} L 160 ${8 + Math.min(sf, sb)} L ${xr + 16} ${40 + Math.min(sf, sb)} Z`} />
      </g>
    </svg>
  );
}

function Footings(props: StepProps) {
  const s = useLocale().strings.civil;
  const { q } = useQ();
  const [fi, setFi] = useState(0);
  const [bi, setBi] = useState(0);
  const front = at(WIDTH_STOPS, fi);
  const back = at(WIDTH_STOPS, bi);
  const f = footing(front, SOIL_KPA.front);
  const b = footing(back, SOIL_KPA.back);
  return (
    <Shell
      {...props}
      id="footings" agent={{ briefs: BRIEFS['civil:footings'] ?? [], apply: (p) => { if (p.front !== undefined) setFi(idx(WIDTH_STOPS, p.front as (typeof WIDTH_STOPS)[number])); if (p.back !== undefined) setBi(idx(WIDTH_STOPS, p.back as (typeof WIDTH_STOPS)[number])); } }}
      text={s.steps.footings}
      version={`${fi}:${bi}`}
      evaluate={() => ({
        ok: f.ok && b.ok,
        message: f.ok && b.ok ? s.footings.ok : !f.ok ? s.footings.sinks(s.footings.front) : s.footings.sinks(s.footings.back),
      })}
    >
      <div className="fsim">
        <FootingFigure front={front} back={back} />
        <p className="fread">
          <strong>{s.footings.front}</strong> {s.footings.pressure(q(f.pressureKpa, 'kPa'), q(SOIL_KPA.front, 'kPa'))} ·{' '}
          <strong>{s.footings.back}</strong> {s.footings.pressure(q(b.pressureKpa, 'kPa'), q(SOIL_KPA.back, 'kPa'))}
        </p>
        <Stops label={s.footings.frontWidth} stops={WIDTH_STOPS} index={fi} onChange={setFi} format={(w) => q(w * 1000, 'mm')} />
        <Stops label={s.footings.backWidth} stops={WIDTH_STOPS} index={bi} onChange={setBi} format={(w) => q(w * 1000, 'mm')} />
      </div>
    </Shell>
  );
}

/* ── 4. Bracing ────────────────────────────────────────────────────────── */

const NEXT_SLOT: Record<Slot, Slot> = { plain: 'brace', brace: 'window', window: 'plain' };

function Bracing(props: StepProps) {
  const s = useLocale().strings.civil;
  const { q } = useQ();
  const [slots, setSlots] = useState<Slot[]>([...EMPTY_WALL]);
  const r = bracing(slots);
  const message = () =>
    r.ok ? s.bracing.ok : r.broken === 'capacity' ? s.bracing.racks(q(r.capacityKn, 'kN'), q(r.demandKn, 'kN', 1)) : r.broken === 'ends' ? s.bracing.ends : s.bracing.windows(WINDOWS_WANTED);
  return (
    <Shell {...props} id="bracing" agent={{ briefs: BRIEFS['civil:bracing'] ?? [], apply: (p) => { if (p.slots) setSlots([...p.slots]); } }} text={s.steps.bracing} version={slots.join(',')} evaluate={() => ({ ok: r.ok, message: message() })}>
      <div className="fsim">
        <BracingWall slots={slots} onToggle={(k) => setSlots((prev) => prev.map((v, j) => (j === k ? NEXT_SLOT[v] : v)))} />
        <p className="fread">
          <strong>{s.bracing.capacity(q(r.capacityKn, 'kN'), q(r.demandKn, 'kN', 1))}</strong> · {s.bracing.count(r.windows, WINDOWS_WANTED)} ·{' '}
          {s.bracing.each(q(PANEL_KN, 'kN'))}
        </p>
        <p className="fread fread--quiet">{s.bracing.hint}</p>
      </div>
    </Shell>
  );
}

function BracingWall({ slots, onToggle }: { slots: Slot[]; onToggle(k: number): void }) {
  const s = useLocale().strings.civil.bracing;
  const { tested, p, phase } = useTested();
  const r = bracing(slots);
  const ratio = r.capacityKn > 0 ? r.demandKn / r.capacityKn : 4;
  // The wall racks in the gust: a little if it is braced enough, a lot if not.
  const lean = tested ? (r.broken === 'capacity' ? Math.min(ratio, 3) * 5 : r.broken === 'ends' ? 7 : 1.2) * Math.sin(p * Math.PI * 0.5 + (1 - p) * 3) * p : 0;
  return (
    <div className={`fwall is-${phase}`}>
      <div className="fwall__wind" aria-hidden="true" style={{ opacity: tested ? 1 - p * 0.4 : 0.3, transform: `translateX(${tested ? p * 18 : 0}px)` }}>
        ≫
      </div>
      <div className="fwall__slots" style={{ transform: `skewX(${-lean}deg)` }} role="group" aria-label={s.wall}>
        {slots.map((slot, k) => (
          <button
            key={k}
            type="button"
            className={`fwall__slot is-${slot}`}
            aria-label={`${k + 1}: ${s.kinds[slot]}`}
            disabled={phase === 'testing'}
            onClick={() => onToggle(k)}
          >
            <span aria-hidden="true" />
          </button>
        ))}
      </div>
      <div className="fwall__legend" aria-hidden="true">
        <span className="is-brace">{s.kinds.brace}</span>
        <span className="is-window">{s.kinds.window}</span>
        <span className="is-plain">{s.kinds.plain}</span>
      </div>
    </div>
  );
}

export const CIVIL_STEPS: Record<CivilStepId, ComponentType<StepProps>> = {
  rafters: Rafters,
  lintel: Lintel,
  footings: Footings,
  bracing: Bracing,
  'civil-ai': CivilAi,
};
